import type {
  AddRowDefault,
  CellAutoFill,
  CellRow,
} from '@erp/item-master-rules';
import { isGridRecord, type GridCellChange } from './as400ExportEditState';
import { gridRecordCellRow } from './as400GridCellRules';
import type { RowMode } from '@erp/item-master-rules';
import { asCellText } from './as400ExportParts';

/**
 * Values the review grid writes for the author: auto-fills, which set a field
 * because another one changed ("when ISTAT is S, CHANGE STATUS FLAG is Y"),
 * and defaults, which a new row starts with. Both land as ordinary edits —
 * pending, tinted, staged by Save — so the author sees and can change them.
 * Neither replaces a rule: a rule stays as the backstop for a value typed back
 * over the fill.
 */

/** One field set to one value. */
export interface FieldChange {
  field: string;
  value: string | null;
}

/** `row` with `changes` applied over it, those fields counting as edited. */
function rowWithChanges(
  row: CellRow,
  changes: readonly FieldChange[],
): CellRow {
  const values = new Map(changes.map((change) => [change.field, change.value]));
  return {
    value: (field) =>
      values.has(field) ? (values.get(field) ?? null) : row.value(field),
    edited: (field) => values.has(field) || row.edited(field),
    as400Value: row.as400Value,
    mode: row.mode,
    itemAdds: row.itemAdds,
    stockUoms: row.stockUoms,
  };
}

function fillApplies(
  fill: CellAutoFill,
  changed: ReadonlySet<string>,
  mode: RowMode,
): boolean {
  if (fill.modes && !fill.modes.includes(mode)) return false;
  return fill.when.some((field) => changed.has(field));
}

/**
 * The extra changes a batch of `changes` to one row calls for. A field the
 * batch itself sets is the author's, never overwritten; a field already
 * holding the filled value needs no write; when two fills name one field the
 * first wins. One pass: a filled value doesn't trigger further fills.
 */
export function autoFillChanges(
  fills: readonly CellAutoFill[],
  changes: readonly FieldChange[],
  row: CellRow,
): FieldChange[] {
  if (fills.length === 0 || changes.length === 0) return [];
  const changed = new Set(changes.map((change) => change.field));
  const landed = rowWithChanges(row, changes);
  // Every field a fill named, written or not: the first fill to name a field
  // claims it, even when the field already holds its value.
  const claimed = new Map<string, string | null>();
  for (const fill of fills) {
    if (!fillApplies(fill, changed, row.mode)) continue;
    for (const [field, value] of Object.entries(fill.fill(landed) ?? {})) {
      if (!changed.has(field) && !claimed.has(field)) claimed.set(field, value);
    }
  }
  return [...claimed].flatMap(([field, value]) =>
    landed.value(field) === value ? [] : [{ field, value }],
  );
}

/**
 * A new row's starting values: `seed` (what the Add-row dialog typed), plus
 * each default the seed doesn't already set. Defaults read the seeded row.
 */
export function withAddRowDefaults(
  defaults: readonly AddRowDefault[],
  seed: Readonly<Record<string, string | null>>,
): Record<string, string | null> {
  const row: CellRow = {
    value: (field) => seed[field] ?? null,
    edited: (field) => field in seed,
    mode: 'add',
  };
  const values: Record<string, string | null> = { ...seed };
  for (const { field, value } of defaults) {
    if (field in values) continue;
    const filled = value(row);
    if (filled !== null) values[field] = filled;
  }
  return values;
}

/**
 * Add a batch's auto-fills to the batch itself, in place, so they land in the
 * same change as the edit that called for them (Handsontable's beforeChange).
 * `recordAt` finds a visual row's grid record; `fields` are the worksheet's
 * editable fields, the only ones a fill may write. A `reset` batch (the
 * grid's reset items) runs only the fills that opt in with `onReset`.
 */
export function autoFillGridChanges(
  changes: Array<GridCellChange | null>,
  allFills: readonly CellAutoFill[],
  recordAt: (row: number) => unknown,
  fields: ReadonlySet<string>,
  { reset = false }: { reset?: boolean } = {},
): void {
  const fills = reset ? allFills.filter((fill) => fill.onReset) : allFills;
  if (fills.length === 0) return;
  for (const [row, rowChanges] of changesByRow(changes)) {
    const record = recordAt(row);
    if (!isGridRecord(record)) continue;
    const extras = autoFillChanges(
      fills,
      rowChanges,
      gridRecordCellRow(record),
    ).filter((extra) => fields.has(extra.field));
    for (const { field, value } of extras) {
      changes.push([row, field, record[field], value]);
    }
  }
}

/** A batch's changes per visual row, cancelled (null) changes left out. */
function changesByRow(
  changes: ReadonlyArray<GridCellChange | null>,
): Map<number, FieldChange[]> {
  const byRow = new Map<number, FieldChange[]>();
  for (const change of changes) {
    const [row, prop, , value] = change ?? [];
    if (typeof row !== 'number' || typeof prop !== 'string') continue;
    const list = byRow.get(row) ?? [];
    list.push({ field: prop, value: asCellText(value) });
    byRow.set(row, list);
  }
  return byRow;
}
